<template>
  <div class="account-coupon"
       :class="{ 'couponList-empty': !state.couponList.length }">
    <div v-if="state.couponList.length > 0"
         class="horizontal header">
      <div class="activity">
        <img :src="activity"
             class="activity-img" />
        <div class="activity-btn">
          <span>{{ $t('AccountInfo.AccountCoupon.enterActivity') }}</span>
          <div class="to-activity">
            <img :src="toActivity"
                 alt=""
                 class="to-icon">
          </div>
        </div>
      </div>
      <div class="coupon-list">
        <div class="list-container"
             :style="{ marginLeft: `calc((-${state.step * couponsWidth}px))` }">
          <template v-for="item of state.couponList"
                    :key="item.id">
            <coupon-item :item-info="item"
                         @get-mycoupon-list="getMycouponList"
                         @get-coupon-list="getCouponList"
                         @open-rule-modal="openRuleModal"></coupon-item>
          </template>
        </div>
        <div class="list-mask"></div>
      </div>
      <div class="pagination">
        <div class="previous-page pagination-item"
             @click="paginationClick('previous', -1)">
          <img v-if="state.step > 0"
               :src="pageDown"
               alt="">
          <img v-else
               :src="prohibitPageDown"
               alt="">
        </div>
        <div class="next-page pagination-item"
             @click="paginationClick('next', 1)">
          <img v-if="canPageDown"
               :src="pageDown"
               alt="">
          <img v-else
               :src="prohibitPageDown"
               alt="">
        </div>
      </div>
    </div>
    <div class="my-coupon">
      <a-tabs v-model:activeKey="activeKey"
              @change="getMycouponList">
        <a-tab-pane key="1"
                    :tab="$t('AccountInfo.AccountCoupon.tabPending')"></a-tab-pane>
        <a-tab-pane key="2"
                    :tab="$t('AccountInfo.AccountCoupon.tabUsed')"></a-tab-pane>
        <a-tab-pane key="3"
                    :tab="$t('AccountInfo.AccountCoupon.tabExpired')"></a-tab-pane>
        <template #leftExtra>
          <img :src="myCoupon"
               alt=""
               class="mycoupon-icon">
        </template>
      </a-tabs>
      <div v-show="state.mycouponList.length > 0"
           id="mycouponList"
           class="mycoupon-list">
        <template v-for="item of state.mycouponList"
                  :key="item.id">
          <mycoupon-item :item-info="item"
                         :active-key="activeKey"
                         @get-mycoupon-list="getMycouponList"
                         @open-rule-modal="openRuleModal"></mycoupon-item>
        </template>
      </div>
      <div v-show="state.mycouponList.length === 0"
           class="mycoupon-default">
        <div class="content">
          <div class="default-text">{{ activeKey === '1' ? $t('AccountInfo.AccountCoupon.noCoupons') : activeKey === '2' ?
            $t('AccountInfo.AccountCoupon.noUsedCoupons') : $t('AccountInfo.AccountCoupon.noExpiredCoupons') }}
          </div>
        </div>
      </div>
    </div>
    <rule-modal :visible="rule.visible"
                :info="rule.info"
                @close-rule-modal="closeRuleModal"></rule-modal>
  </div>
</template>

<script setup lang="ts">
import activity from "./assets/imgs/activity.png"
import toActivity from "./assets/imgs/toActivity.png"
import pageDown from "./assets/imgs/pageDown.png"
import prohibitPageDown from "./assets/imgs/prohibitPageDown.png"
import myCoupon from "./assets/imgs/myCoupon.png"
import CouponItem from "../components/CouponItem.vue"
import MycouponItem from "../components/MycouponItem.vue"
import { onBeforeMount, reactive, ref, nextTick, onBeforeUnmount, watch } from "vue"
import { getNotJoinActivity, getActivityCoupons } from "../api/coupon"
import type { SingleCoupon, ruleInfo } from "../api/interface/couponInterface"
import RuleModal from "../components/RuleModal.vue"

const state = reactive<{
  couponList: SingleCoupon[],
  step: number,
  mycouponList: SingleCoupon[]
}>({
  couponList: [],
  step: 0,
  mycouponList: []
})

function paginationClick(type: string, number: number) {
  if ((type === 'next' && !canPageDown.value) || type === 'previous' && state.step <= 0) return
  state.step += number
}

const activeKey = ref('1')

const getCouponList = async () => {
  const res = await getNotJoinActivity()
  state.couponList = res
  if (state.couponList.length > 0) {
    nextTick(() => {
      getCouponsWidth()
    })
  }
}

const getMycouponList = async () => {
  state.mycouponList = []
  const res = await getActivityCoupons(activeKey.value)
  state.mycouponList = res
}

const couponsWidth = ref(0)
const listContainerWidth = ref(0)
const getCouponsWidth = () => {
  const clientWidth = document.getElementsByClassName('coupon-item')[0]?.clientWidth
  const listWidth = document.getElementsByClassName('list-container')[0]?.clientWidth
  couponsWidth.value = clientWidth ? clientWidth + 20 : 0
  listContainerWidth.value = listWidth || 0
}

onBeforeMount(() => {
  getCouponList()
  getMycouponList()
  window.addEventListener("resize", () => {
    getCouponsWidth()
  })
})

const canPageDown = ref(false)
watch(() => listContainerWidth.value, () => {
  if (couponsWidth.value === 0 || listContainerWidth.value === 0) {
    canPageDown.value = false
    return
  }
  canPageDown.value = (state.couponList.length - state.step) * couponsWidth.value >= listContainerWidth.value
})

watch(() => state.step, () => {
  if (couponsWidth.value === 0 || listContainerWidth.value === 0) {
    canPageDown.value = false
    return
  }
  canPageDown.value = (state.couponList.length - state.step) * couponsWidth.value >= listContainerWidth.value
})

const rule = reactive<{
  info: ruleInfo,
  visible: boolean
}>({
  info: {
    isStackable: false,
    activityScope: "",
    thresholdAmount: undefined
  },
  visible: false
})

function openRuleModal(info: SingleCoupon) {
  const activityScope = [] as string[]
  if (info.activity_scope?.length > 0) {
    info.activity_scope.forEach((item: any) => {
      if (item.name) {
        activityScope.push(item.name)
      }
    })
  }
  if (activityScope.length > 0) {
    rule.info.activityScope = activityScope.join(",")
  } else {
    rule.info.activityScope = ""
  }
  rule.info.isStackable = info.is_stackable
  rule.info.thresholdAmount = info.threshold_amount
  rule.visible = true
}
function closeRuleModal() {
  rule.visible = false
}

onBeforeUnmount(() => {
  window.removeEventListener("resize", () => { })
})
</script>
<style lang="less" scoped>
.account-coupon {
  height: 100%;
  background: url('./assets/imgs/activityBg.png') no-repeat;
  background-size: 100%;
  padding: 0 0 0 20px;

  .horizontal {
    display: flex;
  }

  .header {
    padding: 50px 20px 0 0;

    .activity {
      padding: 50px 100px 38px 80px;
      display: flex;
      flex-direction: column;
      align-items: center;

      .activity-img {
        width: 241px;
        height: 149px;
      }

      .activity-btn {
        margin-top: 29px;
        width: 140px;
        height: 30px;
        line-height: 30px;
        background: #111111;
        border-radius: 15px;
        font-size: 14px;
        font-family: Microsoft YaHei;
        color: #FFFFFF;
        padding-left: 16px;
        position: relative;

        .to-activity {
          width: 26px;
          height: 26px;
          background: #FFFFFF;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: absolute;
          right: 2px;
          top: 2px;

          .to-icon {
            width: 6px;
            height: 10px;
          }
        }
      }
    }

    .coupon-list {
      flex: 1;
      overflow: hidden;
      position: relative;

      .list-container {
        display: flex;
        width: 100%;
        transition: margin-left 0.2s ease;
      }

      .list-mask {
        width: 100px;
        height: 100%;
        position: absolute;
        top: 0;
        right: 0;
        background: linear-gradient(270deg, #F6F6F6, transparent);
      }
    }

    .pagination {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      padding-left: 20px;

      .pagination-item {
        width: 40px;
        height: 40px;
        background: #F1F1F1;
        display: flex;
        align-items: center;
        justify-content: center;
        user-select: none;
      }

      .previous-page {
        margin-right: 2px;
      }

      .next-page {
        img {
          transform: scaleX(-1);
        }
      }
    }
  }

  .my-coupon {
    margin-top: 40px;
    height: calc(100% - 386px);

    .mycoupon-icon {
      height: 26px;
      padding-right: 39px;
    }

    .mycoupon-list {
      display: flex;
      flex-wrap: wrap;
      align-content: flex-start;
      height: calc(100vh - 679px);
      overflow-y: auto;
    }

    .mycoupon-default {
      height: calc(100vh - 679px);
      display: flex;
      justify-content: center;
      align-items: center;

      .content {
        width: 245px;
        height: 192px;
        background: url("./assets/imgs/mycouponDefault.png") no-repeat;
        background-size: 100%;
        position: relative;

        .default-text {
          width: 100%;
          position: absolute;
          bottom: 20px;
          text-align: center;
          font-size: 14px;
          color: #999999;
        }
      }
    }
  }
}

.couponList-empty {
  background: none;

  .my-coupon {
    height: 100%;
    margin-top: 0;

    .mycoupon-list,
    .mycoupon-default {
      height: calc(100vh - 293px);
    }
  }
}
</style>
<style lang="less">
.account-coupon {
  .ant-tabs-nav {
    margin-bottom: 20px;
  }

  .ant-tabs-top>.ant-tabs-nav::before {
    border-bottom: 0px;
  }

  .ant-tabs-tab {
    font-size: 16px;

    .ant-tabs-tab-btn {
      color: #999999;
    }
  }

  .ant-tabs-tab-active {
    font-weight: bold;

    .ant-tabs-tab-btn {
      color: #111111;
    }
  }

  .ant-tabs-tab+.ant-tabs-tab {
    margin: 0 0 0 31px;
  }

  .ant-tabs-ink-bar {
    margin-left: 14px;
    width: 14px !important;
    height: 4px !important;
  }
}
</style>
